// Minimal markdown-to-HTML renderer tailored to the handbook content.
// Avoids 3rd-party deps. Escapes HTML, then applies inline + block formatting.

function escapeHtml(s) {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function slugify(s) {
    return s
        .toLowerCase()
        .replace(/<[^>]+>/g, "")
        .replace(/&[a-z]+;/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export function extractHeadings(md) {
    // Return [{ level, text, slug }] for every #/## in the markdown
    const out = [];
    const seen = new Set();
    for (const raw of md.split("\n")) {
        const m = raw.trim().match(/^(#{1,3})\s+(.*)$/);
        if (!m) continue;
        const text = m[2].replace(/\*\*/g, "").trim();
        let slug = slugify(text);
        if (!slug) continue;
        let unique = slug, n = 2;
        while (seen.has(unique)) unique = `${slug}-${n++}`;
        seen.add(unique);
        out.push({ level: m[1].length, text, slug: unique });
    }
    return out;
}

function inline(text) {
    // images ![alt](src) — must come before links
    text = text.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="handbook-img" loading="lazy" />');
    // bold
    text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    // italics
    text = text.replace(/(^|[\s(])\*([^*\n]+)\*(?=[\s).,!?]|$)/g, "$1<em>$2</em>");
    // inline code
    text = text.replace(/`([^`]+)`/g, "<code>$1</code>");
    // links [text](url)
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    return text;
}

export function renderMarkdown(md, highlight = "") {
    const lines = escapeHtml(md).split("\n");
    let html = "";
    let inUl = false, inOl = false, inBlockquote = false;
    const seenSlugs = new Set();

    const closeLists = () => {
        if (inUl) { html += "</ul>"; inUl = false; }
        if (inOl) { html += "</ol>"; inOl = false; }
    };
    const closeBq = () => { if (inBlockquote) { html += "</blockquote>"; inBlockquote = false; } };

    for (const raw of lines) {
        const line = raw.trim();
        if (line === "") { closeLists(); closeBq(); continue; }

        let m;
        // Horizontal rule
        if (/^---+$/.test(line)) {
            closeLists(); closeBq();
            html += '<hr class="handbook-hr" />';
            continue;
        }
        // Block image (line is only an image)
        if ((m = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/))) {
            closeLists(); closeBq();
            const alt = m[1];
            const src = m[2];
            html += `<figure class="handbook-figure"><img src="${src}" alt="${alt}" loading="lazy" /><figcaption>${alt}</figcaption></figure>`;
            continue;
        }
        if ((m = line.match(/^(#{1,6})\s+(.*)$/))) {
            closeLists(); closeBq();
            const lvl = m[1].length;
            const text = m[2];
            let slug = slugify(text.replace(/\*\*/g, ""));
            if (slug) {
                let unique = slug, n = 2;
                while (seenSlugs.has(unique)) unique = `${slug}-${n++}`;
                seenSlugs.add(unique);
                slug = unique;
            }
            const idAttr = slug ? ` id="${slug}"` : "";
            html += `<h${lvl}${idAttr}>${inline(text)}</h${lvl}>`;
            continue;
        }
        if (line.startsWith("> ")) {
            closeLists();
            if (!inBlockquote) { html += "<blockquote>"; inBlockquote = true; }
            html += `<p>${inline(line.slice(2))}</p>`;
            continue;
        }
        if ((m = line.match(/^(\d+)\.\s+(.*)$/))) {
            closeBq();
            if (inUl) { html += "</ul>"; inUl = false; }
            if (!inOl) { html += "<ol>"; inOl = true; }
            html += `<li>${inline(m[2])}</li>`;
            continue;
        }
        if ((m = line.match(/^[-*]\s+(.*)$/))) {
            closeBq();
            if (inOl) { html += "</ol>"; inOl = false; }
            if (!inUl) { html += "<ul>"; inUl = true; }
            html += `<li>${inline(m[1])}</li>`;
            continue;
        }
        closeLists(); closeBq();
        html += `<p>${inline(line)}</p>`;
    }
    closeLists(); closeBq();

    if (highlight && highlight.trim()) {
        const re = new RegExp(`(${highlight.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
        html = html.replace(/(>)([^<]+)(<)/g, (match, a, content, b) => a + content.replace(re, '<mark class="bscc-highlight">$1</mark>') + b);
    }
    return html;
}
